import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import {
    LayoutDashboard,
    KanbanSquare,
    LogOut,
    ChevronLeft,
    ChevronRight,
    User,
    BrainCircuit,
    Users,
    Briefcase,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

export function Sidebar() {
    const { isOpen, toggle, setOpen } = useSidebar();
    const [showDefaultImage, setShowDefaultImage] = useState(false);
    const { dbUser, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) setOpen(false); // Default closed on mobile
            else setOpen(true); // Default open on desktop
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setOpen]);

    const handleLogout = async () => {
        try {
            navigate('/', { replace: true }); // Navigate first to public route
            await logout();
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    // Role detection
    const isCandidateView = location.pathname.startsWith('/candidate');

    const recruiterItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: KanbanSquare, label: 'Pipeline', path: '/dashboard/pipeline' },
        { icon: Users, label: 'Candidate Pool', path: '/dashboard/pool' },
    ];

    const candidateItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/candidate/dashboard' },
        { icon: Briefcase, label: 'Applications', path: '/candidate/dashboard/applications' },
        { icon: BrainCircuit, label: 'Practice', path: '/candidate/dashboard/practice' },
    ];

    const menuItems = isCandidateView ? candidateItems : recruiterItems;

    // Mobile Overlay Sidebar
    if (isMobile) {
        return (
            <>
                {/* Mobile Sidebar Overlay */}
                <div className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 md:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setOpen(false)} />

                <div className={`fixed top-16 left-0 h-[calc(100vh-64px)] w-64 bg-background border-r border-black/5 dark:border-border z-50 transform transition-transform duration-300 md:hidden flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="p-6 border-b border-border">
                        <h2 className="text-xl font-bold text-foreground">Menu</h2>
                    </div>

                    <nav className="flex-1 p-4 space-y-2">
                        {menuItems.map((item) => {
                            const isRootPath = item.path === '/dashboard' || item.path === '/candidate/dashboard';
                            const isActive = location.pathname === item.path || (!isRootPath && location.pathname.startsWith(item.path));
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive
                                        ? 'bg-primary/10 text-primary border border-primary/20'
                                        : 'text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground'
                                        }`}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                </Link>
                            )
                        })}
                    </nav>

                    <div className="p-4 border-t border-border mt-auto bg-background">
                        <div className="flex items-center gap-3 mb-4 px-2">
                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0">
                                {dbUser?.avatar && !showDefaultImage ? (
                                    <img src={dbUser.avatar} alt="Profile" className="w-full h-full object-cover" onError={() => setShowDefaultImage(true)} />
                                ) : (
                                    <User className="w-5 h-5 text-muted-foreground" />
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{dbUser?.name || 'User'}</p>
                                <p className="text-xs text-muted-foreground truncate">{dbUser?.email}</p>
                            </div>
                        </div>
                        <Button variant="destructive" className="w-full justify-start" onClick={handleLogout}>
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    // Desktop Sidebar
    return (
        <div
            className={`hidden md:flex flex-col h-[calc(100vh-64px)] fixed top-16 left-0 bg-background/50 backdrop-blur-md border-r border-black/5 dark:border-border transition-all duration-300 z-30 ${isOpen ? 'w-64' : 'w-20'
                }`}
        >
            {/* Toggle Button */}
            <button
                onClick={toggle}
                className="absolute -right-3 top-6 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
                {isOpen ? <ChevronLeft className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>

            <nav className="flex-1 p-4 space-y-2 mt-4">
                {menuItems.map((item) => {
                    const isRootPath = item.path === '/dashboard' || item.path === '/candidate/dashboard';
                    const isActive = location.pathname === item.path || (!isRootPath && location.pathname.startsWith(item.path));
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all group relative ${isActive
                                ? 'bg-primary/10 text-primary border border-primary/20'
                                : 'text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground'
                                } ${!isOpen && 'justify-center'}`}
                            title={!isOpen ? item.label : undefined}
                        >
                            <item.icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-primary' : 'group-hover:text-foreground'}`} />
                            {isOpen && <span className="font-medium whitespace-nowrap overflow-hidden transition-all duration-300">{item.label}</span>}
                        </Link>
                    )
                })}
            </nav>

            {/* User Profile Footer */}
            <div className={`p-4 border-t border-black/5 mt-auto transition-all ${!isOpen && 'items-center flex flex-col'}`}>
                <div className={`flex items-center gap-3 mb-4 ${!isOpen && 'justify-center mb-0'}`}>
                    <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0">
                        {dbUser?.avatar && !showDefaultImage ? (
                            <img src={dbUser.avatar} alt="Profile" className="w-full h-full object-cover" onError={() => setShowDefaultImage(true)} />
                        ) : (
                            <User className="w-4 h-4 text-muted-foreground" />
                        )}
                    </div>

                    {isOpen && (
                        <div className="min-w-0 overflow-hidden">
                            <p className="text-sm font-medium text-foreground truncate">{dbUser?.name || 'User'}</p>
                            <p className="text-xs text-muted-foreground truncate">{dbUser?.email}</p>
                        </div>
                    )}
                </div>

                {isOpen ? (
                    <Button variant="secondary" className="w-full justify-start bg-secondary hover:bg-secondary/80 text-secondary-foreground" onClick={handleLogout}>
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                    </Button>
                ) : (
                    <button
                        onClick={handleLogout}
                        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors mt-4"
                        title="Sign Out"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
